import { Platform } from 'react-native';
import { PLATFORM_AUTH_URL } from '../api/endpoints';

// Handoff to the SK Platform's auth app (skplatform-fe) — mirrors
// sk-catalog-frontend's navigation/platformAuthRedirect.ts. Drive owns no
// login/signup/verify/reset screens; every auth action bounces here.

const PRODUCT = 'drive';
const STATE_COOKIE = 'sk_auth_state';

const isWeb = (): boolean => Platform.OS === 'web' && typeof window !== 'undefined';

function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Parent-domain scope on real hosts (e.g. `.softknack.com`, shared with
 * platform.<env>.softknack.com — required for skplatform-fe's own CSRF check
 * to see this cookie), host-only on localhost (cookies ignore port, so it's
 * still shared with the platform on another port there).
 */
function stateCookieDomainAttr(): string {
  const parts = window.location.hostname.split('.');
  return parts.length >= 2 && parts[parts.length - 1] !== 'localhost'
    ? `; domain=.${parts.slice(-2).join('.')}`
    : '';
}

function secureAttr(): string {
  return window.location.protocol === 'https:' ? '; Secure' : '';
}

function readStateCookie(): string | undefined {
  return document.cookie.match(/(?:^|;\s*)sk_auth_state=([^;]+)/)?.[1];
}

function clearStateCookie(): void {
  document.cookie = `sk_auth_state=; Path=/; Max-Age=0${stateCookieDomainAttr()}`;
}

// Guard against firing twice within one page load (e.g. boot probe + a parallel
// 401) — the first call navigates away.
let redirecting = false;

/**
 * Redirect the browser to the platform's auth app, carrying a `product`,
 * `return_to`, and a CSRF `state` nonce (stashed in a short-lived cookie so a
 * malicious page can't forge the redirect origin). Guarded by `redirecting`
 * so a boot probe and a parallel 401 don't both fire a navigation.
 */
export function redirectToPlatformAuth(): void {
  if (!isWeb() || redirecting) return;
  redirecting = true;

  const state = randomState();
  document.cookie = `${STATE_COOKIE}=${state}; Path=/; Max-Age=600; SameSite=Lax${stateCookieDomainAttr()}${secureAttr()}`;

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('product', PRODUCT);
  url.searchParams.set('return_to', window.location.href);
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
}

/**
 * On return from the platform, verify the `state` query param against the
 * cookie stashed before departure. Returns false only when both are present
 * and mismatched (a forged/replayed redirect) — absent params (fresh load,
 * no handoff in progress) pass through.
 */
export function verifyReturnState(): boolean {
  const returnState = new URLSearchParams(window.location.search).get('state');
  const storedState = readStateCookie();
  if (!returnState || !storedState) return true;
  clearStateCookie();
  return returnState === storedState;
}

/**
 * Hand a verification token to the centralized platform verify screen
 * (`?mode=verify&token=…`). The SK Platform owns the ONLY verify /
 * "link expired" screen for every product — so a re-clicked or expired drive
 * link is re-processed there and shows the platform's screen, never a local one.
 * Web-only; no-op on native.
 */
export function redirectToPlatformVerify(token: string): void {
  if (!isWeb() || redirecting) return;
  redirecting = true;

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('mode', 'verify');
  url.searchParams.set('token', token);
  window.location.assign(url.toString());
}

/**
 * Read (and clear) a `?mode=verify&token=…` from the URL — the drive
 * verification email links back here. Returns the token once, then strips the
 * query so a reload / return-trip doesn't re-process a consumed token.
 * Web-only; returns null on native or when absent.
 */
export function takeVerifyToken(): string | null {
  if (!isWeb()) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') !== 'verify') return null;
  const token = params.get('token');
  if (!token) return null;
  window.history.replaceState({}, '', window.location.pathname);
  return token;
}

/**
 * Hand a password-reset token to the centralized platform reset screen
 * (`?mode=reset&token=…`). The SK Platform owns the ONLY reset-password /
 * "link expired" screen for every product — so a drive reset link is
 * re-processed there and shows the platform's screen, never a local one and
 * never a bounce to plain login:
 *   • valid link    → platform's "Reset your password" form
 *   • used/expired  → platform's "link invalid / expired" screen
 * Web-only; no-op on native.
 */
export function redirectToPlatformReset(token: string): void {
  if (!isWeb() || redirecting) return;
  redirecting = true;

  const url = new URL(PLATFORM_AUTH_URL);
  url.searchParams.set('mode', 'reset');
  url.searchParams.set('token', token);
  window.location.assign(url.toString());
}

/**
 * Read (and clear) a `?mode=reset&token=…` from the URL — the drive
 * password-reset email links back here. Returns the token once, then strips the
 * query so a reload doesn't re-process it. Web-only; returns null on native/absent.
 */
export function takeResetToken(): string | null {
  if (!isWeb()) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') !== 'reset') return null;
  const token = params.get('token');
  if (!token) return null;
  window.history.replaceState({}, '', window.location.pathname);
  return token;
}
